import { Label } from "@radix-ui/react-label";
import { useId, useState, type ComponentProps } from "react";
import {
    get,
    useFormContext,
    type FieldError,
    type FieldValues,
    type Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Info } from "lucide-react";
import { useSchema } from "@/v2/hooks/SchemaContext";
import RequiredMark from "./RequiredMark";
import { ZodObject } from "zod";

type FormFieldBaseProps = ComponentProps<typeof Input>;

interface ArPasswordFormFieldProps<
    T extends FieldValues,
> extends FormFieldBaseProps {
    name: Path<T>;
    label: string;
    infoText?: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
}

export function ArPasswordFormField<T extends FieldValues>({
    label,
    name,
    placeholder,
    labelAlign,
    infoText,
    ...rest
}: ArPasswordFormFieldProps<T>) {
    const {
        register,
        formState: { errors },
    } = useFormContext<T>();

    const error = get(errors, name) as FieldError | undefined;
    const id = useId();
    const schema = useSchema();
    const requiredCheck = (schema as ZodObject<any>).shape[name].safeParse("");
    const isRequired = !requiredCheck.success;

    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className={cn("flex flex-col gap-1", labelAlign && labelAlign)}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <Label
                        htmlFor={id}
                        className={cn(
                            "text-sm font-medium",
                            error && "text-destructive",
                        )}
                    >
                        {label}
                        {isRequired && <RequiredMark />}
                    </Label>
                    {infoText && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 text-muted-foreground"
                            title={infoText}
                        >
                            <Info className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </div>

            <div className="relative">
                <Input
                    id={id}
                    placeholder={placeholder}
                    type={showPassword ? "text" : "password"}
                    className={cn(
                        error &&
                            "border-destructive pr-8 focus-visible:ring-destructive",
                    )}
                    {...register(name)}
                    {...rest}
                />
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-muted-foreground"
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={-1}
                >
                    {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                    ) : (
                        <Eye className="h-4 w-4" />
                    )}
                </Button>
            </div>

            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
